"use client";

import For from "@/components/common/For";
import If from "@/components/common/If";
import { BlogCard } from "@/components/ui/Card/Card";
import { Accordion } from "@radix-ui/react-accordion";

import { Plus, X } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { ChevronLeft } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

const categories = {
  Types: ["Blog", "Case Studies"],
  Category: [
    "Flush Wood Veneer",
    "Flush Paintable",
    "Flush Laminates",
    "Stile & Rail",
  ],
  Market: ["Healthcare", "Hospitality", "Education", "Office / Multi-Use"],
  Solution: ["Acoustics", "Aesthetics", "Cleanability", "Durability"],
};

const expandedTags = {
  category: "Category",
  market: "Market",
  solution: "Solution",
  blog: "Blog",
  caseStudy: "Case Studies",
  flushWoodVeneer: "Flush Wood Veneer",
  flushPaintable: "Flush Paintable",
  flushLaminates: "Flush Laminates",
  stileRail: "Stile & Rail",
  durability: "Durability",
  aesthetics: "Aesthetics",
  cleanability: "Cleanability",
  acoustics: "Acoustics",
  healthcare: "Healthcare",
  hospitality: "Hospitality",
  education: "Education",
  officeMultiUse: "Office / Multi-Use",
};

const reverseExpandedTags = Object.fromEntries(
  Object.entries(expandedTags).map(([key, value]) => [value, key])
);

export default function UI({ posts = [] }) {
  const query = useSearchParams();
  const router = useRouter();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const filterResults = useCallback(
    (data, currentSearch = "", currentFilters = []) => {
      const searchValue = currentSearch?.toLowerCase();

      if (!searchValue && !currentFilters?.length) {
        return [...data];
      }

      let filteredData = [...data];

      if (searchValue) {
        filteredData = filteredData.filter((item) =>
          item?.title?.toLowerCase()?.includes(searchValue)
        );
      }

      if (currentFilters.length > 0) {
        if (
          (currentFilters.includes("Case Studies") &&
            !currentFilters.includes("Blog")) ||
          (currentFilters.includes("Blog") &&
            !currentFilters.includes("Case Studies"))
        ) {
          filteredData = filteredData.filter((item) => {
            if (currentFilters.includes("Case Studies")) {
              return item?.contentfulMetadata?.tags?.some(
                (tag) => tag?.name === "Case Study"
              );
            }

            if (currentFilters.includes("Blog")) {
              return item?.__typename === "BlogPost";
            }
          });
        }

        filteredData = filteredData.filter((item) => {
          const allTags = item.contentfulMetadata.tags.map((tag) => tag.name);

          return currentFilters
            .filter((tag) => tag !== "Case Studies" && tag !== "Blog")
            .every((tag) => allTags?.includes(tag));
        });
      }

      return filteredData;
    },
    []
  );

  useEffect(() => {
    setResults(filterResults(posts, search, filters));
  }, [search, filters]);

  useEffect(() => {
    const tags = query.get("tags");
    const newFilters =
      !tags || tags === "all"
        ? []
        : tags
            .split(",")
            .map((tag) => expandedTags[tag.trim()])
            .filter(Boolean);

    setFilters(newFilters);
    setResults(filterResults(posts, search, newFilters));
  }, [query, posts]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleFilter = useCallback(
    (filter) => {
      const hasFilter = filters.includes(filter);
      let newFilters = hasFilter
        ? filters.filter((item) => item !== filter)
        : [...filters, filter];

      // if (documentTypes.includes(filter)) {
      //   newFilters = newFilters.filter(
      //     (item) => !documentTypes.includes(item) || item === filter
      //   );
      // }

      const queryTags =
        newFilters.length === 0
          ? "all"
          : newFilters
              .map((tag) => reverseExpandedTags[tag])
              .filter(Boolean)
              .join(",");

      const searchParams = new URLSearchParams();
      searchParams.set("tags", queryTags);
      router.push(`?${searchParams.toString()}`);
    },
    [filters, router]
  );

  const formatTitle = (title) => {
    const titleArray = title.split("_");
    const id = titleArray.splice(0, 1);
    const theRest = titleArray.join("_");
    return `${id} ${theRest?.replace(/_/g, " ")?.replace(/-/g, " ")?.replace(".pdf", "")}`;
  };
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof ref.current?.classList === "undefined") return;

    if (window.innerWidth < 768) {
      ref.current.dataset.lenisPrevent = "true";
    } else {
      delete ref.current.dataset.lenisPrevent;
    }
  }, [isOpen]);

  console.log(results);

  return (
    <div className="resource-grid px-gutter min-h-[90vh] mb-20">
      <button onClick={() => setIsOpen(!isOpen)} className="flex md:hidden">
        Filters <ChevronRight />
      </button>
      <div className={`filters stack gap-6 ${isOpen ? "open" : ""}`} ref={ref}>
        <button onClick={() => setIsOpen(false)} className="flex md:hidden">
          <ChevronLeft />
          Close
        </button>
        <header className="stack gap-2 bg-[#F2F2F2]   p-4">
          <input placeholder="Search" onChange={handleSearch} value={search} />

          <div className="flex gap-2 flex-wrap">
            {filters.map((filter) => {
              return (
                <button
                  key={filter}
                  onClick={() => {
                    handleFilter(filter);
                  }}
                  className="border border-gold text-gold px-2 py-1 text-sm flex gap-2  items-center  "
                >
                  {filter} <X size={12} />
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 justify-between">
            <If condition={filters?.length}>
              <button
                onClick={() => setFilters([])}
                className="flex gap-1 text-gray"
              >
                {/* <X /> */}
                Reset
              </button>
            </If>

            <span className="font-bold">
              {results?.length !== 1
                ? `${results?.length} documents`
                : "1 document"}
            </span>
          </div>
        </header>

        <For
          each={Object.entries(categories)}
          render={([category, items]) => {
            return (
              <>
                <h2 className="font-bold uppercase">{category}</h2>
                <ul className="stack gap-4 px-4" key={category}>
                  <For
                    each={items}
                    render={(item) => (
                      <li key={item}>
                        <label className="flex gap-2 items-center leading-none">
                          <input
                            type="checkbox"
                            className="text-gold"
                            checked={filters?.includes(item)}
                            onChange={() => handleFilter(item)}
                          />
                          {item}
                        </label>
                      </li>
                    )}
                  />
                </ul>
              </>
            );
          }}
        />
      </div>

      <div>
        <If condition={!results?.length}>
          <p>No results found</p>
        </If>

        <If condition={results?.length > 0}>
          <div className="results flex flex-wrap gap-12">
            {results?.map((resource) => {
              console.log(resource);
              return (
                <BlogCard
                  image={
                    resource?.heroImage?.url ||
                    resource?.heroImage?.cdn_url ||
                    resource?.blogHeroImage?.url ||
                    resource?.blogHeroImage?.cdn_url ||
                    resource?.imageLink ||
                    resource?.heroImage?.[0]?.cdn_url ||
                    resource?.cardImage?.[0]?.cdn_url ||
                    resource?.brandfolderHeroImage?.[0]?.cdn_url
                  }
                  title={resource?.title}
                  date={resource?.publishDate}
                  description={resource?.industry || ''}
                  handle={
                    resource?.__typename === "BlogPost"
                      ? `/blog/${resource?.handle}`
                      : `/case-studies/${resource?.handle}`
                  }
                  postType={resource?.__typename}
                  isBlog={true}
                />
              );
            })}
          </div>
        </If>
      </div>
    </div>
  );
}

const Details = ({ title, children }) => {
  const [currentOpen, setCurrentOpen] = useState(true);
  return (
    <Accordion.Root
      className="w-full"
      collapsible
      onValueChange={(value) => {
        setCurrentOpen(value);
      }}
      defaultValue={title}
    >
      <Accordion.Item
        value={title}
        className="border-b border-b-white px-4 w-full block"
      >
        <Accordion.Header>
          <Accordion.Trigger className={`stack gap-2 w-full pb-1`}>
            <div className="flex justify-between items-center w-full">
              <h2 className="font-bold uppercase">{title}</h2>
              <Plus
                className={`transtion-all ease-in-out duration-300 relative block ${
                  currentOpen ? "rotate-45" : ""
                }`}
                size={32}
                color="black"
                strokeWidth={0.5}
              />
            </div>
          </Accordion.Trigger>
        </Accordion.Header>

        <Accordion.Content
          className={`
                        overflow-hidden transition-all duration-300 ease-in-out
                        data-[state=open]:animate-slideDown
                        data-[state=closed]:animate-slideUp
                      `}
        >
          <div className="py-4">{children}</div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
